import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  List,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import type { ScanResponse } from '@/models/Scans'
import { fetchLatestScanResultsByNeedId } from '@/api/scansAPI'
import { AccessibleTooltip } from '@/components/AccessibleTooltip'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useStep } from '../hooks/useStep'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useWizardData } from '../hooks/useWizardData'
import { categorizeResults } from '../utils/scanCategorization'
import { COLORS } from '../theme'
import { stepHeadingSx, stepDescriptionSx } from '../styles'
import OnboardingMatchRow from '../components/OnboardingMatchRow'

export default function ScanResults() {
  const {
    fundingNeedId,
    scanResultId,
    setScanResultId,
    maxStepReached,
    setStaleFrom,
    goToStep,
  } = useOnboardingV2()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const [, setWizardData] = useWizardData()

  const parsed = scanResultId ? Number(scanResultId) : null
  const initialParsed = parsed !== null && !isNaN(parsed) ? parsed : null
  const [selectedId, setSelectedId] = useState<number | null>(initialParsed)
  const initialSelectedRef = useRef(initialParsed)

  const {
    data: results,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['scanResultsByNeedId', fundingNeedId],
    queryFn: () => fetchLatestScanResultsByNeedId(token, fundingNeedId!),
    enabled: !!token && !!fundingNeedId,
    retry: 3,
  })

  // Clear stale selectedId if it doesn't exist in the results (e.g. after re-scan)
  useEffect(() => {
    if (
      selectedId !== null &&
      results &&
      !results.some((r: ScanResponse) => r.id === selectedId)
    ) {
      setSelectedId(null)
    }
  }, [results, selectedId])

  const handleSelect = useCallback(
    (id: number) => setSelectedId((prev) => (prev === id ? null : id)),
    [],
  )

  const categories = useMemo(() => categorizeResults(results ?? []), [results])
  const selectableMatches = useMemo(
    () =>
      categories
        .filter((c) => c.key !== 'relational')
        .reduce((sum, c) => sum + c.fullCount, 0),
    [categories],
  )
  const hasEnoughMatches = selectableMatches >= 3

  const beforeNextNoMatches = useCallback(async () => {
    setWizardData({ scanCompleted: false, fundingMode: 'guided' })
    setStaleFrom(STEPS.SCAN_RUNNING)
    await goToStep(STEPS.FUNDING_GUIDED)
    return false as const
  }, [setWizardData, setStaleFrom, goToStep])

  const beforeNext = useCallback(async () => {
    setScanResultId(selectedId !== null ? String(selectedId) : null)
    setWizardData({ scanHadResults: true })
    if (
      selectedId !== initialSelectedRef.current &&
      maxStepReached >= STEPS.DRAFT_GENERATING
    ) {
      setStaleFrom((prev) =>
        prev === null
          ? STEPS.DRAFT_GENERATING
          : Math.min(prev, STEPS.DRAFT_GENERATING),
      )
    }
  }, [selectedId, setScanResultId, setWizardData, maxStepReached, setStaleFrom])

  useStep(
    hasEnoughMatches
      ? { isValid: selectedId !== null, beforeNext }
      : { isValid: true, beforeNext: beforeNextNoMatches },
  )

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: COLORS.primary }} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          We could not load your matches. Please go back and try running the
          scan again.
        </Alert>
      </Box>
    )
  }

  const totalMatches = categories.reduce((sum, c) => sum + c.fullCount, 0)

  if (!hasEnoughMatches) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h5" sx={stepHeadingSx}>
            No matches found
          </Typography>
          <Typography
            variant="body2"
            sx={{ ...stepDescriptionSx, maxWidth: 600, fontStyle: 'italic' }}
          >
            No funding opportunities are a good match for the need you
            described.
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            ...stepDescriptionSx,
            textAlign: 'center',
            maxWidth: 600,
            mx: 'auto',
            color: COLORS.primary,
            fontWeight: 500,
            fontSize: '18px',
            mb: 0,
          }}
        >
          Don’t worry - we can help!
        </Typography>
        <Typography
          variant="body1"
          sx={{
            ...stepDescriptionSx,
            textAlign: 'center',
            maxWidth: 600,
            mx: 'auto',
            color: COLORS.primary,
          }}
        >
          Click continue below and the system will help you create a funding
          request that’s aligned to available grant opportunities.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" sx={stepHeadingSx}>
        Your funding matches
      </Typography>
      <Typography variant="body1" sx={stepDescriptionSx}>
        <span style={{ fontWeight: 500 }}>
          We found {totalMatches} good{' '}
          {totalMatches === 1 ? 'match' : 'matches'} for you!{' '}
        </span>
        Once you’re all set up, you’ll be able to explore them all in detail.
        For this onboarding process, please select just one so we can show you
        how application drafting works.
      </Typography>
      <Typography variant="body1" sx={stepDescriptionSx}>
        Select a match below to generate your first application draft, then
        click Continue.
      </Typography>

      {categories.map((category) => (
        <Box key={category.key}>
          <Typography variant="h5" component="h2" sx={{ mt: 5 }}>
            {category.label}
            {category.key === 'relational' && (
              <>
                {' '}
                <AccessibleTooltip
                  title={
                    <>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{ px: 1, pt: 2, fontStyle: 'italic' }}
                      >
                        What are Relational Opportunities?
                      </Typography>
                      <Typography variant="body2" sx={{ px: 1, py: 2 }}>
                        Relational opportunities are with Funders who don't have
                        an open grants process, but are open to being
                        approached. You are less likely to be successful with
                        Relational Funders, and they often receive a large
                        number of requests. Read through the information
                        carefully, and only approach them if you believe there
                        is a strong strategic alignment.
                      </Typography>
                    </>
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#f5f5f9',
                        color: 'rgba(0, 0, 0, 0.87)',
                        maxWidth: 600,
                        border: '1px solid #dadde9',
                      },
                    },
                  }}
                >
                  <HelpOutlineIcon
                    sx={{ fontSize: '20px', color: COLORS.primary }}
                  />
                </AccessibleTooltip>
              </>
            )}
          </Typography>
          {category.description && category.key !== 'relational' && (
            <Typography
              variant="body2"
              sx={{ color: COLORS.secondary, mt: 0.5, mb: 1 }}
            >
              {category.description}
            </Typography>
          )}
          {category.key === 'relational' && (
            <Alert severity="info" sx={{ mt: 1.5, mb: 1 }}>
              Once your onboarding setup is complete, for relational funding
              opportunities you’ll be able to click a button to have the system
              draft an introduction letter to send to the funder. For now,
              select a non-relational opportunity above to see a demonstration
              of application drafting.
            </Alert>
          )}
          <List>
            <Divider />
            {category.results.map((match) => (
              <OnboardingMatchRow
                key={match.id}
                match={match}
                selected={match.id === selectedId}
                selectable={category.key !== 'relational'}
                onSelect={() => handleSelect(match.id)}
              />
            ))}
          </List>
          {category.fullCount > category.results.length && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: COLORS.primary,
                  fontStyle: 'italic',
                  mb: 1,
                }}
              >
                {category.fullCount - category.results.length} more{' '}
                {category.fullCount - category.results.length === 1
                  ? 'match'
                  : 'matches'}{' '}
                in this category available to explore after onboarding.
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      <Box>
        <Typography variant="h5" component="h2" sx={{ mt: 5 }}>
          Future Planning
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.secondary, mt: 0.5 }}>
          Annual subscribers can see 12 months of matches to support future
          planning and build out their funding calendar. You can explore this
          feature after you've completed onboarding.
        </Typography>
      </Box>
    </Box>
  )
}
